// importing core libraries
import React, { useEffect, useRef, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

// importing third party libraries
import { IoClose } from "react-icons/io5";
import { FaStar } from "react-icons/fa";
import { ClipLoader } from "react-spinners";
import { Info } from "lucide-react";

//importing custom hooks
import { useThunk } from "../../hooks/useThunk.js";
import  useForm  from "../../hooks/useForm.js";

// importing assets
import attachicon from "../../assets/Thread/Attach.svg";
import sendicon from "../../assets/Thread/Send.svg";
import icon from "../../assets/Thread/@.svg";
import waiting from "../../assets/Thread/nomessages.jpg";

//importing components
import ArrowButton from "../../components/Form/ArrowButton";
import Input from "../../components/Form/Inputfield";
import DragUpload from "../Form/DragUpload";
import SystemMessage from "./SystemMessage";
import CustomFileIcon from "../CustomFileIcon.jsx";
import LoginButton from "../Form/LoginButton";
import LoginInput from "../Form/LoginInput.jsx";
import Checkbox from "../Form/Checkbox";
import DeliveryScreeningNotice from "./DeliveryScreeningNotice.jsx";

// import dispatchers, thunks, functions, utills
import {
    acceptOrder,
    clearThreadChats,
    fetchThreadById,
    fetchUserThreadChats,
    fetchUserThreadSystemNotifications,
    requestRevision,
    setBulkThreads,
    setSelectedChatsPage,
    setSelectedFormType,
    setThreadBulkChats,
} from "../../store";
import { getTokenPayload } from "../../utils/getTokenPayload";
import showToast from "../../utils/toastNotifications.js";
import {
    formatHH_MM_SS,
    formatOrderCreatedDate,
    formatYYYY_MM_DD,
    formatYYYY_MM_DD_HH_MM_A,
} from "../../utils/dateFormating.js";
import {
    groupMessagesByDate,
    sortGroupdArray,
} from "../../utils/groupMessagesByDate.js";
import { ADITIONAL_SERVICE_LABELS, TURNITIN_REPORT_TOOLTIP, orderStatus, roles, systemNotifications } from "../../constants/commonConstants.js";
import useFirebaseFileUpload from "../../hooks/useFirebaseFileUpload.js";
import { getFileExtension } from "../../utils/getFileExtension.js";
import { USER_INPUT_FIELD_TYPES } from "../../constants/userInputFieldNames.js";
import { currencyFormatter, formatPagesSlidesRequirement } from "../../utils/formatting.js";
import { twMerge } from "tailwind-merge";
import MakeExtraPaymentModal from "./MakeExtraPaymentModal.jsx";
import { AditionalServiceCostCalculator } from "../../utils/aditionalServiceCostCalculator.js";
import { refreshInProgressOrderCount } from "../Header/useInProgressOrderCount.js";
import { sanitizeRichText } from "../../utils/richText.js";
import { canTipWriter } from "../../utils/writerTipEligibility.js";

// const values
const SIZE = 5000;
const MAX_VISIBLE_REFERENCE_DOCUMENTS = 2;
const TYPING_IDLE_TIMEOUT = 1800;
const TYPING_VISIBLE_TIMEOUT = 3000;
const MESSAGE_BOTTOM_THRESHOLD = 80;

const findMessageScrollContainer = (element) => {
    let current = element;

    while (current && current !== document.body) {
        const overflowY = window.getComputedStyle(current).overflowY;
        if (/auto|scroll|overlay/.test(overflowY) && current.scrollHeight > current.clientHeight) {
            return current;
        }
        current = current.parentElement;
    }

    return element;
};

const getDeliveryFileUrl = (file) => typeof file === "string" ? file : file?.url;

const getDeliveryFileName = (file) => {
    if (!file || typeof file === "string") return "";
    return file.name || file.fileName || file.originalName || "";
};

const DELIVERY_FILE_TYPE_LABELS = {
    final_file: "Final File",
    editor_review: "Editor Review",
    ai_report: "AI Report",
    similarity_report: "Similarity Report",
    turnitin_ai_report: "Turnitin AI Report",
    turnitin_similarity_report: "Turnitin Similarity Report",
    copyleaks_ai_report: "Copyleaks AI Report",
    originality_ai_report: "Originality AI Report",
    gptzero_ai_report: "GPTZero AI Report",
    zerogpt_ai_report: "ZeroGPT AI Report",
    additional_file: "Additional File",
};

const getDeliveryFileType = (file) =>
    typeof file === "string" ? "final_file" : file?.fileType || "additional_file";

const getDeliveryFileTypeLabel = (file) =>
    DELIVERY_FILE_TYPE_LABELS[getDeliveryFileType(file)] || DELIVERY_FILE_TYPE_LABELS.additional_file;

const hasParticipantReadMessage = (message, selectedThread, currentUserId) => {
    const messageCreatedAt = new Date(message?.createdAt).getTime();

    if (!messageCreatedAt || Number.isNaN(messageCreatedAt)) {
        return false;
    }

    const customerId = selectedThread?.user?._id?.toString() || selectedThread?.user?.toString();
    const senderId = message?.sender?.toString();
    const isCustomerMessage = message?.senderModel === "User" || (senderId === customerId && !message?.isAdmin);

    if (!isCustomerMessage) {
        return selectedThread?.lastRead?.some((entry) => (
            customerId &&
            entry?.userId?.toString() === customerId &&
            new Date(entry?.lastReadTimestamp).getTime() >= messageCreatedAt
        ));
    }

    return selectedThread?.lastRead?.some((entry) => {
        const entryUserId = entry?.userId?.toString();
        const lastReadAt = new Date(entry?.lastReadTimestamp).getTime();

        return (
            entryUserId &&
            entryUserId !== currentUserId?.toString() &&
            entryUserId !== message?.sender?.toString() &&
            lastReadAt >= messageCreatedAt
        );
    });
};

const MessageReadReceipt = ({ message, selectedThread, currentUserId }) => {
    const isOwnMessage = message?.sender?.toString() === currentUserId?.toString();

    if (!isOwnMessage) {
        return null;
    }

    const isRead = hasParticipantReadMessage(message, selectedThread, currentUserId);

    return (
        <span
            className={twMerge(
                "ml-2 inline-flex items-center gap-1 not-italic",
                isRead ? "font-semibold text-[#2563eb]" : "text-gray"
            )}
            title={isRead ? "Read" : "Sent"}
        >
            <span aria-hidden="true">
                {isRead ? <>&#10003;&#10003;</> : <>&#10003;</>}
            </span>
            {isRead && <span>Read</span>}
        </span>
    );
};

const ReferenceDocumentsList = ({ documents = [] }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    if (!documents.length) {
        return <span className="text-gray">-</span>;
    }

    const visibleDocuments = isExpanded
        ? documents
        : documents.slice(0, MAX_VISIBLE_REFERENCE_DOCUMENTS);
    const hiddenCount = documents.length - MAX_VISIBLE_REFERENCE_DOCUMENTS;

    return (
        <div className="w-full min-w-0">
            <div
                className={twMerge(
                    "grid w-full min-w-0 grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3",
                    isExpanded && documents.length > 3 && "max-h-[220px] overflow-y-auto pr-1"
                )}
            >
                {visibleDocuments.map((url, index) => (
                    <CustomFileIcon
                        extension={getFileExtension(url)}
                        key={`${url}-${index}`}
                        url={url}
                        className="w-full"
                        contentClassName="w-full"
                    />
                ))}
            </div>
            {hiddenCount > 0 && (
                <button
                    type="button"
                    className="mt-2 text-left font-inter text-[12px] font-semibold leading-4 text-[#3157c8] hover:text-[#1d4ed8]"
                    onClick={(event) => {
                        event.stopPropagation();
                        setIsExpanded((current) => !current);
                    }}
                >
                    {isExpanded ? "Show fewer files" : `+${hiddenCount} more file${hiddenCount > 1 ? "s" : ""}`}
                </button>
            )}
        </div>
    );
};

const MessageList = ({
    socket,
    roleRef,
    userIdRef,
    selectedThreadRef,
    threasdsRef,
    chatRef,
    roomIdSearchParam,
    threadIdSearchParam,
    refetchSelectedThreadAndOrderFromQueryParams,
    refetchSelectedThreadAndOrder,
    isFetchUserOrderByRoomId = false,
    isFetchThreadById = false,
}) => {

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const tokenPayload = getTokenPayload();
    const thread = useSelector((state) => state.thread);    

    const scrollToLatestMessage = (behavior = "smooth") => {
        const scrollContainer = messageActiveScrollContainerRef.current || messageScrollContainerRef.current;
        if (scrollContainer) {
            scrollContainer.scrollTop = scrollContainer.scrollHeight;
        }
        messageEndRef.current?.scrollIntoView({ behavior, block: "end" });
    };

    /* === > defining local states <=== */
    const messageEndRef = useRef(null);
    const messageScrollContainerRef = useRef(null);
    const messageActiveScrollContainerRef = useRef(null);
    const messageIsNearBottomRef = useRef(true);
    const messageAutoScrollPendingRef = useRef(true);
    const messageAutoScrollInterruptedRef = useRef(false);
    const previousMessageViewRef = useRef(null);
    const previousMessageSnapshotRef = useRef({ threadId: null, lastId: null, length: 0 });
    const systemNotificationEndRef = useRef(null);
    const systemNotificationScrollContainerRef = useRef(null);
    const systemNotificationScrollPendingRef = useRef(true);
    const previousSystemNotificationThreadRef = useRef(null);
    const messageInputRef = useRef(null);
    const messageSendingRef = useRef(false);
    const typingIdleTimeoutRef = useRef(null);
    const typingVisibleTimeoutRef = useRef(null);
    const [selectedReveision, setSlectedReveision] = useState(null)
    const multipleRevisions = []
        
    /* ------- CHATTING RELATED LOCAL STATES ---------- */
    const groupedInboxMessagesByDate = useMemo(
        () => groupMessagesByDate(thread?.selectedThreadChat || []),
        [thread?.selectedThreadChat]
    );
    const [text, setText] = useState("");    
    const [replyToMessage, setReplyToMessage] = useState(null);
    const [typingParticipantLabel, setTypingParticipantLabel] = useState("");
    const [isMessageSending, setIsMessageSending] = useState(false);
    // State to store deliver documents
    const [uploadFiles, setUploadFiles] = useState([]);
    
    // chat pagination
    const [pagination, setPagination] = useState({
        page: 1,
        size: SIZE,
    });
    
    // getting custom hooks
    const [
        doFetchUserThreadChats,
        isFetchUserThreadChatsLoading,
        fetchUserThreadChatsError,
    ] = useThunk(fetchUserThreadChats);
    
    const [
        doFetchUserThreadSystemNotifications,
        isFetchUserThreadSystemNotificationsLoading,
        fetchUserThreadSystemNotificationsError,
    ] = useThunk(fetchUserThreadSystemNotifications);
    
    const [
        doRequestRevision,
        isRequestRevision,
        errorRequestRevision,
    ] = useThunk(requestRevision);

    const [
        doAcceptOrder,
        isAcceptOrder,
        errorAcceptOrder,
    ] = useThunk(acceptOrder);
    
    // uploading files
    const [
        doUploadRefrencesToFirebase,
        isUploadRefrencesToFirebaseLoading,
        uploadRefrencesToFirebaseError,
    ] = useFirebaseFileUpload();
    
    const quickReviewComments = [
        "Perfect, thanks.",
        "Good quality, I appreciate it.",
        "Excellent work and right on time.",
        "Everything looks great.",
        "Well done, thank you."
    ];

    // accept order propterties
    const [rating, setRating] = useState(5);
    const [feedback, setFeedback] = useState("");

    // request revision form properties ====================================================>
    const {
        formData,
        errors,
        touched,
        handleInputChange,
        handleBlur,
        validateForm,
        setTouched,
        setFormData,
    } = useForm(
        {
            requestRevisionRequirements: "",
        },
        {
            requestRevisionRequirements: true
        },
        {
            requestRevisionRequirements: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,

        }
    );

    const selectedOrder = thread?.selectedOrder;
    const orderRequirements = selectedOrder?.orderRequirements;
    const paymentDetails = selectedOrder?.paymentDetils;
    const referenceDocuments = orderRequirements?.referenceDocuments || [];
    const pagesSlidesRequirement = formatPagesSlidesRequirement(
        orderRequirements?.pages,
        orderRequirements?.slides
    );
    const renderPlainField = ({ label, value, className = "" }) => (
        <li className={twMerge("flex flex-col gap-y-0.5 border-b border-[#eaf0f8] py-2 text-[13px] xl:flex-row xl:flex-wrap xl:items-baseline xl:justify-between xl:gap-x-3", className)}>
            <span className="font-inter font-medium text-[#637189]">
                {label}
            </span>
            <span className="break-words text-left font-inter font-semibold text-secondary xl:text-right">
                {value || "-"}
            </span>
        </li>
    );
    const renderAssignedWriterValue = (writerId) => {
        if (!writerId) return "not confirmed";

        return <span>{writerId}</span>;
    };
    const getAdditionalServiceDisplayLabel = (service) => {
        if (service?.label === ADITIONAL_SERVICE_LABELS.SPECIFIC_WRITER) {
            return "Preferred Writer";
        }

        return service?.label;
    };
    const getAdditionalServiceTooltip = (service) => (
        [ADITIONAL_SERVICE_LABELS.TURNITIN_AI, ADITIONAL_SERVICE_LABELS.SIMILARITY_AI].includes(service?.label)
            ? TURNITIN_REPORT_TOOLTIP
            : ""
    );
    const getVisibleAdditionalServices = (services = []) =>
        services.filter((service) => !["Grammarly Report", "Writing Process Report"].includes(service?.label));

    const orderDetailFields = [
        { label: "Order ID", value: selectedOrder?.id || "-" },
        { label: "Assigned Expert ID", value: renderAssignedWriterValue(selectedOrder?.writer?.id) },
        { label: "Receipt ID", value: paymentDetails?.id || "-" },
        { label: "Payment Method", value: paymentDetails?.method || "-" },
        {
            label: "Payment Date",
            value: formatYYYY_MM_DD(paymentDetails?.createdAt || "-") || "-",
            highlight: true,
        },
        {
            label: "Initial Payment",
            value: currencyFormatter.format(paymentDetails?.amount || 0) || "$ 00.00",
        },
        {
            label: "Additional Payment",
            value: currencyFormatter.format(selectedOrder?.extraPrice || 0) || "$ 00.00",
        },
        {
            label: "Total Payment",
            value:
                currencyFormatter.format(
                    (paymentDetails?.amount || 0) + (selectedOrder?.extraPrice || 0)
                ) || "$ 00.00",
            highlight: true,
            className: "sm:col-span-2 xl:col-span-1",
        },
    ];

    const orderRequirementFields = [
        { label: "Writing Type", value: orderRequirements?.writingType || "-" },
        { label: "Type of Paper", value: orderRequirements?.typeOfPaper || "-" },
        { label: "Academic Level", value: orderRequirements?.academicLevel || "-" },
        { label: "Type of English", value: orderRequirements?.typeOfEnglish || "-" },
        {
            label: "Deadline",
            value: formatYYYY_MM_DD_HH_MM_A(orderRequirements?.deadline || "") || "-",
            highlight: true,
        },
        { label: "Spacing", value: orderRequirements?.spacing || "-" },
        {
            label: pagesSlidesRequirement.label,
            value: pagesSlidesRequirement.value,
        },
        { label: "Subject", value: orderRequirements?.subject || "-" },
        { label: "Topic", value: orderRequirements?.topic || "-" },
        { label: "Reference Style", value: orderRequirements?.referenceStyle || "-" },
        orderRequirements?.previousOrderId && {
            label: "Previous Order ID",
            value: orderRequirements?.previousOrderId || "-",
        },
        orderRequirements?.requestedWriterId && {
            label: "Preferred Writer ID",
            value: orderRequirements?.requestedWriterId || "-",
        },
    ].filter(Boolean);

    // fetch system notifications
    useEffect(() => {
        if (thread?.selectedThreadId) {
            fetchUserThreadSystemNotificationsMethod();
            setUploadFiles([]);
        }
    }, [thread?.selectedThreadId]);

    // fetch user thread chats according to pagination modal
    useEffect(() => {
        if (!thread?.selectedThreadId || thread?.selectedFormType !== "message") {
            return;
        }

        fetchUserThreadChatsMethod();
        setUploadFiles([]);
    }, [thread?.selectedThreadId, thread?.selectedFormType, pagination.page, pagination.size]);

    useEffect(() => {setUploadFiles([])}, [thread?.selectedFormType]);

    // If a request is made to open the accept order form or revision request form regardless of that particular operation completed already; Returns back to the System Messages section
    useEffect(() => {
        if(thread?.selectedFormType === "request-revision" && thread?.selectedOrder?.status === orderStatus.IN_REVISION){
            dispatch(setSelectedFormType("system"));
        }else if(thread?.selectedFormType === "accept-order" && thread?.selectedOrder?.status === orderStatus.COMPLETED){
            dispatch(setSelectedFormType("system"));
        }
    }, [thread?.selectedFormType])

    // Track the actual scroll owner. Nested thread containers vary by viewport,
    // so the element that overflows is not always the message list itself.
    useEffect(() => {
        if (thread?.selectedFormType !== "message") {
            return;
        }

        let removeListeners = () => {};
        const attachScrollListenerTimer = setTimeout(() => {
            const scrollContainer = findMessageScrollContainer(messageScrollContainerRef.current);
            if (!scrollContainer) return;

            messageActiveScrollContainerRef.current = scrollContainer;
            const updateBottomState = () => {
                const distanceFromBottom =
                    scrollContainer.scrollHeight - scrollContainer.clientHeight - scrollContainer.scrollTop;
                const isNearBottom = distanceFromBottom <= MESSAGE_BOTTOM_THRESHOLD;
                messageIsNearBottomRef.current = isNearBottom;
                if (!isNearBottom) messageAutoScrollInterruptedRef.current = true;
            };
            const handleUserScrollIntent = () => {
                messageAutoScrollInterruptedRef.current = true;
            };

            scrollContainer.addEventListener("scroll", updateBottomState, { passive: true });
            scrollContainer.addEventListener("wheel", handleUserScrollIntent, { passive: true });
            scrollContainer.addEventListener("touchstart", handleUserScrollIntent, { passive: true });
            scrollContainer.addEventListener("pointerdown", handleUserScrollIntent, { passive: true });

            removeListeners = () => {
                scrollContainer.removeEventListener("scroll", updateBottomState);
                scrollContainer.removeEventListener("wheel", handleUserScrollIntent);
                scrollContainer.removeEventListener("touchstart", handleUserScrollIntent);
                scrollContainer.removeEventListener("pointerdown", handleUserScrollIntent);
            };
        }, 0);

        return () => {
            clearTimeout(attachScrollListenerTimer);
            removeListeners();
        };
    }, [
        groupedInboxMessagesByDate,
        thread?.selectedFormType,
        thread?.selectedThreadId,
        isFetchUserThreadChatsLoading,
    ]);

    // Position a newly opened thread at the latest message. Afterwards, follow
    // new messages only while the customer remains near the bottom, except that
    // a message sent by the customer is always revealed.
    useEffect(() => {
        const isMessageView = thread?.selectedFormType === "message";
        const threadId = thread?.selectedThreadId || null;
        const messages = thread?.selectedThreadChat || [];
        const latestMessage = messages[messages.length - 1];
        const snapshot = {
            threadId,
            lastId: latestMessage?._id?.toString() || null,
            length: messages.length,
        };
        const previousSnapshot = previousMessageSnapshotRef.current;
        const openedMessageView = previousMessageViewRef.current !== "message" && isMessageView;
        const changedThread = previousSnapshot.threadId !== threadId;

        previousMessageViewRef.current = thread?.selectedFormType;

        if (!isMessageView) {
            messageAutoScrollPendingRef.current = true;
            messageAutoScrollInterruptedRef.current = false;
            previousMessageSnapshotRef.current = snapshot;
            return;
        }

        if (openedMessageView || changedThread || isFetchUserThreadChatsLoading) {
            messageAutoScrollPendingRef.current = true;
        }

        if (isFetchUserThreadChatsLoading) {
            previousMessageSnapshotRef.current = snapshot;
            return;
        }

        const appendedMessage =
            !changedThread &&
            snapshot.length > previousSnapshot.length &&
            snapshot.lastId !== previousSnapshot.lastId;
        const sentByCurrentUser =
            appendedMessage &&
            latestMessage?.sender?.toString() === tokenPayload?._id?.toString();
        const shouldScroll =
            messageAutoScrollPendingRef.current ||
            sentByCurrentUser ||
            (appendedMessage && messageIsNearBottomRef.current);

        previousMessageSnapshotRef.current = snapshot;
        if (!shouldScroll || (!messageEndRef.current && !messageScrollContainerRef.current)) {
            return;
        }

        messageAutoScrollInterruptedRef.current = false;
        const scrollIfUninterrupted = (behavior) => {
            if (!messageAutoScrollInterruptedRef.current) scrollToLatestMessage(behavior);
        };
        const immediateScrollTimer = setTimeout(() => {
            scrollIfUninterrupted("auto");
        }, 0);
        const settledScrollTimer = setTimeout(() => {
            scrollIfUninterrupted("smooth");
            messageAutoScrollPendingRef.current = false;
        }, 120);

        return () => {
            clearTimeout(immediateScrollTimer);
            clearTimeout(settledScrollTimer);
        };
    }, [
        groupedInboxMessagesByDate,
        thread?.selectedFormType,
        thread?.selectedThreadId,
        thread?.selectedThreadChat,
        isFetchUserThreadChatsLoading,
        tokenPayload?._id,
    ]);

    // Open each thread's notification history at its latest event. Once the
    // initial positioning is complete, do not override manual scrolling.
    useEffect(() => {
        if (thread?.selectedFormType !== "system") {
            systemNotificationScrollPendingRef.current = true;
            return;
        }

        if (previousSystemNotificationThreadRef.current !== thread?.selectedThreadId) {
            previousSystemNotificationThreadRef.current = thread?.selectedThreadId;
            systemNotificationScrollPendingRef.current = true;
        }

        if (isFetchUserThreadSystemNotificationsLoading) {
            systemNotificationScrollPendingRef.current = true;
            return;
        }

        if (!systemNotificationScrollPendingRef.current || !systemNotificationEndRef.current) {
            return;
        }

        const scrollToLatestNotification = () => {
            if (systemNotificationScrollContainerRef.current) {
                systemNotificationScrollContainerRef.current.scrollTop =
                    systemNotificationScrollContainerRef.current.scrollHeight;
            }
            systemNotificationEndRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
        };
        const immediateScrollTimer = setTimeout(scrollToLatestNotification, 0);
        const settledScrollTimer = setTimeout(() => {
            scrollToLatestNotification();
            systemNotificationScrollPendingRef.current = false;
        }, 120);

        return () => {
            clearTimeout(immediateScrollTimer);
            clearTimeout(settledScrollTimer);
        };
    }, [
        thread?.selectedFormType,
        thread?.selectedThreadId,
        thread?.selectedThreadNotifications,
        isFetchUserThreadSystemNotificationsLoading,
    ]);

    /*
     *
     * ===================== CHATTING RELATED SIDE EFFECTS DEFINITIONS =============================================================================
     *
    */

    // Clear pending attachments when the active chat data changes.
    useEffect(() => {
        setUploadFiles([]);
    }, [thread?.selectedThreadChat])

    useEffect(() => {
        const handleUserTyping = ({ roomId, userId }) => {
            if (
                roomId?.toString() !== thread?.selectedThread?._id?.toString() ||
                userId?.toString() === userIdRef.current?.toString()
            ) {
                return;
            }

            setTypingParticipantLabel(getTypingParticipantLabel(userId));

            if (typingVisibleTimeoutRef.current) {
                clearTimeout(typingVisibleTimeoutRef.current);
            }

            typingVisibleTimeoutRef.current = setTimeout(() => {
                setTypingParticipantLabel("");
            }, TYPING_VISIBLE_TIMEOUT);
        };

        const handleUserStoppedTyping = ({ roomId, userId }) => {
            if (
                roomId?.toString() !== thread?.selectedThread?._id?.toString() ||
                userId?.toString() === userIdRef.current?.toString()
            ) {
                return;
            }

            setTypingParticipantLabel("");
        };

        socket.on("userTyping", handleUserTyping);
        socket.on("userStoppedTyping", handleUserStoppedTyping);

        return () => {
            socket.off("userTyping", handleUserTyping);
            socket.off("userStoppedTyping", handleUserStoppedTyping);

            if (typingVisibleTimeoutRef.current) {
                clearTimeout(typingVisibleTimeoutRef.current);
            }
            if (typingIdleTimeoutRef.current) {
                clearTimeout(typingIdleTimeoutRef.current);
            }
        };
    }, [socket, thread?.selectedThread?._id])

    /*
     *
     * I/O HANDLING METHODS/ SUBMIT METHODS AND OTHER UTILITY METHODS ================================================>
     *
     */

    // Button click for system messages
    const handleButtonClick = (formType, revisionReference) => {
        
        if(formType === "pay-now"){
            navigate(`/secure-payment/${thread?.selectedOrder?._id}`);
        }

        // revision reference will be a object for a deliverable reference or will be a strign for revision reference
        if(revisionReference && typeof revisionReference === 'string'){
            setSlectedReveision(multipleRevisions?.filter(it => it?._id === revisionReference)[0]);
        }else if(revisionReference && typeof revisionReference === 'object'){
            setSlectedReveision(revisionReference)
        }

        dispatch(setSelectedFormType(formType));
    };

    const handleDragUpload = (uploadedFiles) => {
        // Handle file uploads
        setUploadFiles((prev) => [...prev, ...uploadedFiles]);
    };

    const getTypingParticipantLabel = (userId) => {
        const typingUserId = userId?.toString();
        const customerId = thread?.selectedThread?.user?._id?.toString() || thread?.selectedThread?.user?.toString();
        const writerId = thread?.selectedThread?.writer?._id?.toString() || thread?.selectedThread?.writer?.toString();

        if (typingUserId === customerId) return "Customer";
        if (typingUserId === writerId) return "Writer";
        return "Admin";
    };

    const emitStopTyping = () => {
        if (!thread?.selectedThread?._id || !userIdRef.current) return;

        socket.emit("stopTyping", {
            roomId: thread?.selectedThread?._id,
            userId: userIdRef.current,
        });
    };

    const queueTypingIdleStop = () => {
        if (typingIdleTimeoutRef.current) {
            clearTimeout(typingIdleTimeoutRef.current);
        }

        typingIdleTimeoutRef.current = setTimeout(() => {
            emitStopTyping();
        }, TYPING_IDLE_TIMEOUT);
    };

    const handleMessageTextChange = (event) => {
        setText(event.target.value);
        event.target.style.height = "auto";
        event.target.style.height = `${event.target.scrollHeight}px`;

        if (event.target.value?.trim()) {
            socket.emit("typing", {
                roomId: thread?.selectedThread?._id || "",
                userId: userIdRef.current || "",
            });
            queueTypingIdleStop();
        } else {
            emitStopTyping();
        }
    };

    const renderTypingIndicator = () => typingParticipantLabel && (
        <div className="absolute -top-7 left-4 font-inter text-[12px] font-medium text-[#64748b]">
            {typingParticipantLabel} is typing...
        </div>
    );

    useEffect(() => {
        setReplyToMessage(null);
    }, [thread?.selectedThread?._id]);

    const messageSenderLabel = (message) => {
        if (!message) return "an earlier message";
        if (message?.sender?.toString() === tokenPayload?._id?.toString()) return "You";
        if (message?.isAdmin) return "Support";
        return message?.senderModel === "Writer" ? "Writer" : "Customer";
    };
    const messageAuthorLabel = (message) => messageSenderLabel(message) === "You" ? "your message" : messageSenderLabel(message);

    const messagePreview = (message) => message?.content?.trim()
        || (message?.attachments?.length ? "Attachment" : "Message unavailable");

    const renderReplyReference = (message) => {
        if (!message?.replyTo) return null;
        const original = thread?.selectedThreadChat?.find(
            (candidate) => candidate?._id?.toString() === message.replyTo?.toString()
        );

        return (
            <button
                type="button"
                className="mb-2 block w-full rounded-r-[8px] border-l-[3px] border-primary bg-white/70 px-3 py-2 text-left"
                onClick={() => original && document.getElementById(`order-message-${original._id}`)?.scrollIntoView({ behavior: "smooth", block: "center" })}
            >
                <span className="block text-[11px] font-semibold text-secondary">Reply to {messageAuthorLabel(original)}</span>
                <span className="mt-0.5 block max-w-[520px] truncate text-[12px] text-gray">{messagePreview(original)}</span>
            </button>
        );
    };

    const renderReplyComposerPreview = () => replyToMessage && (
        <div className="absolute bottom-full left-0 right-6 mb-2 flex items-center gap-3 rounded-[10px] border border-medium-gray bg-white px-3 py-2 shadow-sm">
            <div className="min-w-0 flex-1">
                <strong className="block text-[11px] text-secondary">Replying to {messageAuthorLabel(replyToMessage)}</strong>
                <span className="block truncate text-[12px] text-gray">{messagePreview(replyToMessage)}</span>
            </div>
            <button type="button" className="shrink-0 text-[12px] font-semibold text-primary" onClick={() => setReplyToMessage(null)}>Cancel</button>
        </div>
    );

    // method to be used to send inbox messages
    const handleSendMessage = async() => {
        if (messageSendingRef.current) return;

        const messageText = text;
        const selectedFiles = uploadFiles;
        const roomId = thread?.selectedThread?._id || "";
        const userId = userIdRef?.current || "";

        if (!messageText?.trim() && selectedFiles.length < 1) return;

        messageSendingRef.current = true;
        setIsMessageSending(true);

        try {
            let downloadURLs = [];

            if (selectedFiles.length > 0) {
                const fileUploadResult = await doUploadRefrencesToFirebase(selectedFiles, "message");
                if (!fileUploadResult.success) {
                    showToast("error", fileUploadResult?.error?.message || "Failed to upload attachments");
                    return;
                }
                downloadURLs = fileUploadResult?.uploadedUrls || [];
            }

            const messagePayload = {
                roomId,
                userId,
                text: messageText,
                role: roleRef?.current?.toLowerCase() || "",
                files: downloadURLs,
                replyTo: replyToMessage?._id || null,
            };

            await new Promise((resolve, reject) => {
                const timeoutId = setTimeout(() => {
                    socket.off("messageResponse", handleConfirmation);
                    reject(new Error("We could not confirm delivery. Please reopen Messages or refresh before sending it again."));
                }, 20000);

                function handleConfirmation(confirmedMessage) {
                    const confirmedFiles = confirmedMessage?.attachments || [];
                    const isMatchingMessage =
                        confirmedMessage?.roomId?.toString() === roomId.toString() &&
                        confirmedMessage?.userId?.toString() === userId.toString() &&
                        confirmedMessage?.text === messageText &&
                        JSON.stringify(confirmedFiles) === JSON.stringify(downloadURLs);

                    if (!isMatchingMessage) return;

                    clearTimeout(timeoutId);
                    socket.off("messageResponse", handleConfirmation);
                    resolve();
                }

                socket.on("messageResponse", handleConfirmation);
                socket.emit("message", messagePayload);
            });

            setText((currentText) => currentText === messageText ? "" : currentText);
            setUploadFiles((currentFiles) => currentFiles === selectedFiles ? [] : currentFiles);
            setReplyToMessage(null);
            emitStopTyping();
            setTimeout(() => {
                if (messageInputRef.current && messageInputRef.current.value === "") {
                    messageInputRef.current.style.height = "";
                }
            }, 0);
        } catch (error) {
            showToast("error", error?.message || "Message could not be sent. Please try again.");
        } finally {
            messageSendingRef.current = false;
            setIsMessageSending(false);
        }
    };

    // request revision submission
    const handleRequestRevision = async() => {
        // Mark all fields as touched       
        setTouched({requestRevisionRequirements: true});

        if (!validateForm()) {
            showToast("error", "Please fill all required fields");
            return;
        }else {
            requestRevisionMethod();
        }

        // else if(uploadFiles?.length < 1){
        //     showToast("error", "Please Upload the Deliverables");
        //     return;
        // }
    }

    // submit request revision
    const requestRevisionMethod = async() => {

        const fileUploadResult = await doUploadRefrencesToFirebase(
            uploadFiles,
            "request-revisions"
        );

        if (!fileUploadResult.success) {
            console.error("Upload Error:", fileUploadResult.error);
            showToast("error", fileUploadResult?.error?.message || "Failed to upload files");
            return;
        }

        const downloadURLs = fileUploadResult.uploadedUrls;

        
        const requestRevisionData = {
            ...formData,
            requirements: formData.requestRevisionRequirements,
            orderId: thread?.selectedOrder?._id,
            documents: downloadURLs,
        };

        // Call the request revision thunk
        const result = await doRequestRevision(requestRevisionData);

        if (result?.success) {

            refreshInProgressOrderCount();

            // refetch the thread and order details in order to update-rerender UI elements, after a state changing operation of an order 
            // invoking methods based on if query parameters exists or if query parametrs does not exist
            // query paramerts exists if this thread page loaded from my order table : view order selected
            // query parametrs does not exist if this thread page loaded as general option
            if (roomIdSearchParam && threadIdSearchParam) {
                refetchSelectedThreadAndOrderFromQueryParams();
            } else {
                refetchSelectedThreadAndOrder();
            }

            showToast("success", "Revision Requested Successfully");
            setFormData({requestRevisionRequirements: ''});
            setUploadFiles([]);
            dispatch(setSelectedFormType("message"));
        } else {
            console.log("Error placing order", result?.error);
            showToast("error", result?.error?.message);
        }
    }

    // accept order submission
    const handleQuickReviewSelect = (comment) => {
        const trimmedFeedback = feedback.trim();

        if (!trimmedFeedback) {
            setFeedback(comment);
            return;
        }

        if (trimmedFeedback.includes(comment)) {
            return;
        }

        setFeedback(`${trimmedFeedback} ${comment}`);
    };

    const handleAcceptOrder = async() => {       
        const acceptOrderData = {
            orderId: thread?.selectedOrder?._id,
            customerFeedback : feedback,
            rating
        };

        // Call the accept order thunk
        const result = await doAcceptOrder(acceptOrderData);

        if (result?.success) {

            refreshInProgressOrderCount();

            // refetch the thread and order details in order to update-rerender UI elements, after a state changing operation of an order 
            // invoking methods based on if query parameters exists or if query parametrs does not exist
            // query paramerts exists if this thread page loaded from my order table : view order selected
            // query parametrs does not exist if this thread page loaded as general option
            if (roomIdSearchParam && threadIdSearchParam) {
                refetchSelectedThreadAndOrderFromQueryParams();
            } else {
                refetchSelectedThreadAndOrder();
            }

            showToast("success", "Order Accepted Successfully");
            setRating(5);
            setFeedback('');
            dispatch(setSelectedFormType("message"));

        } else {
            console.log("Error placing order", result?.error);
            showToast("error", result?.error?.message);
        }
    }

    /*
     *
     * DATA FETCHING METHODS ================================================================================================>
     *
     */

    // Fetch all user thread chats
    const fetchUserThreadChatsMethod = async () => {
        if (!thread?.selectedThreadId) {
            return;
        }

        try {
            const result = await doFetchUserThreadChats({
                page: pagination.page,
                size: pagination.size,
                threadId: thread?.selectedThreadId,
            });

            if (result.success) {

                chatRef.current = result?.response?.data?.chats;
                socket.emit("thread-read", {
                    threadId: thread?.selectedThreadId,
                    userId: userIdRef.current,
                });
                
                // clear the unread message count in client side: cleare the unread count of matching record; restore the record
                const prevThreads = thread?.threads;            
                const newThreads = prevThreads.map((threadParameter) => {
                    if(threadParameter?._id?.toString() === thread?.selectedThreadId?.toString()){                        
                        return {...threadParameter, lastRead: threadParameter?.lastRead?.map((item) => {                            
                            if(item?.userId?.toString() === userIdRef.current?.toString()){                                
                                return {...item, unreadCount: 0};
                            }
                            return item;
                        })}
                    }
                    return threadParameter;
                })
                threasdsRef.current = newThreads;
                dispatch(setBulkThreads(newThreads));
            }

            if (!result.success) {
                showToast("error", result.error?.message);
            }
        } catch {
            showToast("error", "Error occurred during fetching chats.");
        }
    };

    // Fetch all user thread system notifications
    const fetchUserThreadSystemNotificationsMethod = async () => {
        try {
            const result = await doFetchUserThreadSystemNotifications({
                threadId: thread?.selectedThreadId,
            });

            if (!result.success) {
                showToast("error", result.error?.message);
            }
        } catch {
            showToast("error", "Error occurred during fetching chats.");
        }
    };

    const renderDetailsPortal = (children) => createPortal(
        <div className="fixed inset-0 z-[100] bg-black/30 px-3 py-4 sm:px-6 lg:px-8">
            <div className="relative mx-auto flex h-full min-h-0 w-full max-w-[1000px] flex-col overflow-hidden rounded-[12px] bg-white shadow-[0_24px_80px_rgba(15,23,42,0.25)]">
                <button
                    className="absolute right-4 top-4 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-white text-blue-500 underline shadow-[0_8px_24px_rgba(15,23,42,0.12)]"
                    onClick={() => {
                        dispatch(setSelectedFormType("system"));
                    }}
                >
                    <IoClose className="w-[24px] h-auto text-secondary" />
                </button>
                <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin scrollbar-webkit px-3 py-4 font-roboto sm:px-[44px] sm:py-[44px]">
                    {children}
                </div>
            </div>
        </div>,
        document.body
    );

    return (
        <div className="flex flex-col flex-grow h-[100%] mb-0 pb-12 overflow-auto scrollbar-none">
            {thread?.selectedFormType === "view-requirements" ? (
                renderDetailsPortal(
                    <div className="flex justify-center flex-col bg-white rounded-md font-roboto">
                        <h2 className="text-lg font-bold mb-4 mt-2 font-roboto text-[18px] leading-5 text-primary text-center bg-light-blue py-3">
                            Requirement Summary
                        </h2>
                        <ul className="space-y-2 w-full border border-medium-gray p-6 rounde-[10px] font-medium">
                            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4 font-medium text-level-3 leading-5">
                                 <li className="text-secondary">
                                    Writing Type
                                </li>
                                <li className="text-gray">
                                    {thread?.selectedOrder?.orderRequirements
                                        ?.writingType || "-"}
                                </li>
                                <li className="text-secondary">
                                    Type of Paper
                                </li>
                                <li className="text-gray">
                                    {thread?.selectedOrder?.orderRequirements
                                        ?.typeOfPaper || "-"}
                                </li>
                                {/* <li className="text-secondary">
                                    Remove AI content
                                </li>
                                <li className="text-gray">
                                    <Checkbox
                                        name="removeAiContent"
                                        checked={thread?.selectedOrder?.orderRequirements?.removeAiContent}
                                        onChange={()=>{}}
                                        label="Humanize"
                                        containerStyle=""
                                    /> 
                                </li> */}
                                <li className="text-secondary">
                                    Academic Level
                                </li>
                                <li className="text-gray">
                                    {thread?.selectedOrder?.orderRequirements
                                        ?.academicLevel || "-"}
                                </li>
                                <li className="text-secondary">
                                    Type of English
                                </li>
                                <li className="text-gray">
                                    {thread?.selectedOrder?.orderRequirements
                                        ?.typeOfEnglish || "-"}
                                </li>
                                <li className="text-secondary">Deadline</li>
                                <li className="text-gray">
                                    {formatYYYY_MM_DD_HH_MM_A(
                                        thread?.selectedOrder?.orderRequirements
                                            ?.deadline || "-"
                                    )}
                                </li>
                                <li className="text-secondary">Spacing</li>
                                <li className="text-gray">
                                    {thread?.selectedOrder?.orderRequirements
                                        ?.spacing || "-"}
                                </li>
                                <li className="text-secondary">{pagesSlidesRequirement.label}</li>
                                <li className="text-gray">
                                    {pagesSlidesRequirement.value || "-"}
                                </li>
                                <li className="text-secondary">Subject</li>
                                <li className="text-gray">
                                    {thread?.selectedOrder?.orderRequirements
                                        ?.subject || "-"}
                                </li>
                                <li className="text-secondary">Topic</li>
                                <li className="text-gray">
                                    {thread?.selectedOrder?.orderRequirements
                                        ?.topic || "-"}
                                </li>
                                <li className="text-secondary">
                                    Reference Style
                                </li>
                                <li className="text-gray">
                                    {thread?.selectedOrder?.orderRequirements
                                        ?.referenceStyle || "-"}
                                </li>
                                <li className="text-secondary">
                                    Reference Document
                                </li>
                                <li className="text-gray">
                                    {/* {thread?.selectedOrder?.orderRequirements
                                        ?.referenceDocument
                                        ? thread?.selectedOrder
                                            ?.orderRequirements
                                            ?.referenceDocument[0]
                                        : "-"} */}
                                    {
                                        thread?.selectedOrder?.orderRequirements?.referenceDocuments?.length > 0 && (
                                            <div className="mt-2">
                                                <ReferenceDocumentsList
                                                    documents={thread?.selectedOrder?.orderRequirements?.referenceDocuments}
                                                />
                                            </div>
                                        )
                                    }
                                    {
                                        thread?.selectedOrder?.orderRequirements?.referenceDocuments?.length < 1 && '-'
                                    }
                                </li>
                            </ul>
                        </ul>
                    </div>
                )
            ) : thread?.selectedFormType === "pay-now" ? (
                navigate("/secure-payment")
            ) : ["review-order", "review-order-latest"].includes(thread?.selectedFormType) ? (
                (() => {
                    const completedReviewRevisions = thread?.selectedOrder?.revisions?.filter(
                        (revision) => revision.status === "COMPLETED" && revision.finalDeliverables?.length > 0
                    ) || [];
                    const latestCompletedReviewRevision = completedReviewRevisions[completedReviewRevisions.length - 1];
                    const shouldShowLatestReview = thread?.selectedFormType === "review-order-latest";
                    const reviewRevision = shouldShowLatestReview ? latestCompletedReviewRevision : selectedReveision?.obj || latestCompletedReviewRevision;
                    const isRevisionReview = Boolean(reviewRevision);
                    const reviewDeliveryNote = isRevisionReview
                        ? reviewRevision?.deliveryNotes || "-"
                        : thread?.selectedOrder?.deliveryNotes || "-";
                    const reviewDeliverables = isRevisionReview
                        ? reviewRevision?.finalDeliverables || []
                        : thread?.selectedOrder?.finalDeliverables || [];
                    const finalFiles = reviewDeliverables.filter((submission) => getDeliveryFileType(submission) === "final_file");
                    const editorReviewFiles = reviewDeliverables.filter((submission) => getDeliveryFileType(submission) === "editor_review");
                    const additionalFiles = reviewDeliverables.filter((submission) => getDeliveryFileType(submission) === "additional_file");
                    const reportFiles = reviewDeliverables.filter((submission) => {
                        const fileType = getDeliveryFileType(submission);
                        return fileType !== "final_file" && fileType !== "editor_review" && fileType !== "additional_file";
                    });
                    const finalSubmissionFilesCount = finalFiles.length + reportFiles.length + additionalFiles.length;
                    const orderSubject = thread?.selectedOrder?.orderRequirements?.subject || thread?.selectedOrder?.orderRequirements?.topic || "-";
                    const renderDeliveryFile = (submission, index) => {
                        const url = getDeliveryFileUrl(submission);
                        const fileTypeLabel = getDeliveryFileTypeLabel(submission);

                        return (
                            <div key={`${url}-${index}`} className="flex min-w-0 flex-col gap-0.5">
                                {fileTypeLabel && (
                                    <span className="font-inter text-[10px] font-medium leading-3 text-gray">
                                        {fileTypeLabel}
                                    </span>
                                )}
                                <CustomFileIcon
                                    extension={getFileExtension(url)}
                                    url={url}
                                    name={getDeliveryFileName(submission)}
                                    contentClassName="p-1 sm:p-1.5"
                                />
                            </div>
                        );
                    };
                    const renderDeliveryGroup = (title, submissions, isPrimary = false, showTitle = true) => {
                        if (!submissions.length) return null;

                        return (
                            <div className={isPrimary ? "space-y-1.5" : "mt-3 space-y-1.5"}>
                                {showTitle && (
                                    <h3 className="font-roboto text-[13px] font-semibold leading-4 text-primary">
                                        {title}
                                    </h3>
                                )}
                                <div className="flex flex-wrap gap-2">
                                    {submissions.map(renderDeliveryFile)}
                                </div>
                            </div>
                        );
                    };

                    return renderDetailsPortal(
                        <div className="overflow-y-auto">
                            <div className="relative flex flex-col bg-white px-3 py-3 font-roboto sm:px-5 lg:px-6">
                                <div className="border-b border-medium-gray pb-2 pr-9">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <h2 className="font-roboto text-[17px] font-bold leading-5 text-primary">
                                            Review Order
                                        </h2>
                                        <span className="rounded-full bg-light-blue px-2.5 py-0.5 font-inter text-[11px] font-semibold text-blue-shades">
                                            Delivered
                                        </span>
                                        {editorReviewFiles.length > 0 && (
                                            <span className="rounded-full border border-[#BFDBFE] bg-[#EFF6FF] px-2.5 py-0.5 font-inter text-[11px] font-semibold text-[#1D4ED8]">
                                                Editor reviewed
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <section className="mt-3 rounded-[8px] border border-medium-gray bg-white p-3">
                                    <h3 className="font-roboto text-[13px] font-semibold leading-4 text-primary">
                                        Order Summary
                                    </h3>
                                    <div className="mt-2 grid grid-cols-1 gap-2 font-inter text-[12px] leading-4 sm:grid-cols-3">
                                        <div>
                                            <p className="font-semibold text-secondary">Order ID</p>
                                            <p className="mt-0.5 text-gray">{thread?.selectedOrder?.id || "-"}</p>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-secondary">Order Subject</p>
                                            <p className="mt-0.5 text-gray">{orderSubject}</p>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-secondary">Delivery Note</p>
                                            <p className="mt-0.5 whitespace-pre-wrap break-words text-gray">{reviewDeliveryNote}</p>
                                        </div>
                                    </div>
                                </section>

                                {reviewDeliverables.length > 0 && <DeliveryScreeningNotice />}

                                {editorReviewFiles.length > 0 && (
                                    <section className="mt-3 rounded-[8px] border border-[#BFDBFE] bg-[#F8FBFF] p-3">
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                            <h3 className="font-roboto text-[13px] font-semibold leading-4 text-primary">
                                                Editor Review
                                            </h3>
                                            <span className="rounded-full bg-[#DBEAFE] px-2.5 py-0.5 font-inter text-[11px] font-semibold text-[#1D4ED8]">
                                                Editor reviewed
                                            </span>
                                        </div>
                                        <div className="mt-2">
                                            {renderDeliveryGroup("Editor Review", editorReviewFiles, true, false)}
                                        </div>
                                    </section>
                                )}

                                <section className="mt-3 rounded-[8px] border border-medium-gray bg-white p-3">
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <h3 className="font-roboto text-[13px] font-semibold leading-4 text-primary">
                                            Final Submissions
                                        </h3>
                                        <span className="font-inter text-[11px] font-medium text-gray">
                                            {finalSubmissionFilesCount || 0} file{finalSubmissionFilesCount === 1 ? "" : "s"}
                                        </span>
                                    </div>

                                    {finalSubmissionFilesCount > 0 ? (
                                        <div className="mt-2">
                                            {renderDeliveryGroup("Final File", finalFiles, true, false)}
                                            {renderDeliveryGroup("Reports", reportFiles, finalFiles.length < 1)}
                                            {renderDeliveryGroup("Additional Files", additionalFiles, finalFiles.length < 1 && reportFiles.length < 1)}
                                        </div>
                                    ) : (
                                        <p className="mt-2 font-inter text-[12px] leading-4 text-gray">-</p>
                                    )}
                                </section>

                                {(thread?.selectedOrder?.status === orderStatus.ORDER_DELIVERED || canTipWriter(thread?.selectedOrder)) && (
                                    <section className="mt-3 rounded-[8px] border border-medium-gray bg-white p-2.5">
                                        {thread?.selectedOrder?.status === orderStatus.ORDER_DELIVERED && (
                                            <p className="font-inter text-[12px] font-medium leading-4 text-secondary">
                                                Please accept the order or request a revision within the review period. If no action is taken, the order will be automatically completed.
                                            </p>
                                        )}
                                        <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
                                            {canTipWriter(thread?.selectedOrder) && (
                                                <button
                                                    type="button"
                                                    className="flex h-[36px] items-center justify-center rounded-[5px] border border-blue-shades bg-white px-4 font-roboto text-[13px] font-semibold text-blue-shades transition-colors duration-200 hover:bg-light-blue"
                                                    onClick={() => {
                                                        dispatch(setSelectedFormType("tip-writer"));
                                                    }}
                                                >
                                                    Tip Writer
                                                </button>
                                            )}
                                            {thread?.selectedOrder?.status === orderStatus.ORDER_DELIVERED && (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="flex h-[36px] items-center justify-center rounded-[5px] border border-blue-shades bg-white px-4 font-roboto text-[13px] font-semibold text-blue-shades transition-colors duration-200 hover:bg-light-blue"
                                                        onClick={() => {
                                                            dispatch(setSelectedFormType("request-revision"));
                                                        }}
                                                    >
                                                        Request Revision
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="flex h-[36px] items-center justify-center rounded-[5px] bg-blue-shades px-4 font-roboto text-[13px] font-semibold text-white transition-colors duration-200 hover:bg-secondary"
                                                        onClick={() => {
                                                            dispatch(setSelectedFormType("accept-order"));
                                                        }}
                                                    >
                                                        Accept Order
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </section>
                                )}
                            </div>
                        </div>
                    );
                })()
            ) : thread?.selectedFormType === "view-revision" ? (
                renderDetailsPortal(
                    <div className="flex justify-center flex-col bg-white rounded-md font-roboto">
                        <h2 className="text-lg font-bold mb-4 mt-2 font-roboto text-[18px] leading-5 text-primary text-center bg-light-blue py-3">
                            Revision Summary
                        </h2>
                        <ul className="space-y-2 w-full border border-medium-gray p-6 rounde-[10px] font-medium">
                            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4 font-medium text-level-3 leading-5">
                                <li className="text-secondary">Revision ID</li>
                                <li className="text-gray">
                                    {selectedReveision?.obj
                                        ?.id || "-"}
                                </li>
                                <li className="text-secondary">Requirements</li>
                                <li className="text-gray">
                                    {selectedReveision?.obj
                                        ?.requirements
                                        ? selectedReveision?.obj.requirements[0]
                                        : "-"}{" "}
                                </li>
                                <li className="text-secondary">
                                    Additional Documents
                                </li>
                                <li className="text-gray">
                                    {/* {thread?.selectedOrder?.revisions?.slice(-1)
                                        ?.documents
                                        ? thread?.selectedOrder?.revisions?.slice(
                                            -1
                                          )?.documents[0]
                                        : "-"} */}
                                    {
                                        selectedReveision?.obj?.documents?.length > 0 && (
                                            <>
                                                <div className="flex flex-row gap-2 flex-wrap mt-2">
                                                    {
                                                        selectedReveision?.obj?.documents?.map((url, index) => {
                                                            return(
                                                                <>
                                                                    <CustomFileIcon extension={getFileExtension(url)} key={index} url={url}/>
                                                                </>
                                                            )
                                                        })
                                                    }
                                                </div>
                                            </>
                                        )
                                    }
                                    {
                                        selectedReveision?.obj.documents?.length < 1 && '-'
                                    }
                                </li>
                            </ul>
                        </ul>
                    </div>
                )
            ) : thread?.selectedFormType === "view-complaint" ? (
                renderDetailsPortal(
                    <div className="flex justify-center flex-col bg-white rounded-md font-roboto">
                        <h2 className="text-lg font-bold mb-4 mt-2 font-roboto text-[18px] leading-5 text-primary text-center bg-light-blue py-3">
                            Complaint Summary
                        </h2>
                        <ul className="space-y-2 w-full  border border-medium-gray p-6 rounde-[10px] font-medium">
                            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4 font-medium text-level-3 leading-5">
                                <li className="text-secondary">Complaint ID</li>
                                <li className="text-gray">
                                    {thread?.selectedOrder?.complaint?.id || "-"}
                                </li>
                                <li className="text-secondary">
                                    Complaint Date
                                </li>
                                <li className="text-gray">
                                    {formatYYYY_MM_DD(
                                        thread?.selectedOrder?.complaint
                                            ?.createdAt || "-"
                                    ) || "-"}{" "}
                                </li>
                                <li className="text-secondary">
                                    Complaint Subject
                                </li>
                                <li className="text-gray">
                                    {thread?.selectedOrder?.complaint
                                        ?.subject || "-"}
                                </li>
                                <li className="text-secondary">Description</li>
                                <li className="text-gray">
                                    {thread?.selectedOrder?.complaint
                                        ?.description || "-"}
                                </li>
                                <li className="text-secondary">
                                    Preferred Solution
                                </li>
                                <li className="text-gray">
                                    {thread?.selectedOrder?.complaint
                                        ?.preferedResolution || "-"}
                                </li>
                                <li className="text-secondary">
                                    Contact Method
                                </li>
                                <li className="text-gray">
                                    {thread?.selectedOrder?.complaint
                                        ?.contactMethod || "-"}
                                </li>
                                <li className="text-secondary">
                                    Reference Document
                                </li>
                                <li className="text-gray">
                                    {/* {thread?.selectedOrder?.complaint
                                        ?.referenceDocument || "-"} */}
                                    {
                                        thread?.selectedOrder?.complaint?.referenceDocuments?.length > 0 && (
                                            <div className="mt-2">
                                                <ReferenceDocumentsList
                                                    documents={thread?.selectedOrder?.complaint?.referenceDocuments}
                                                />
                                            </div>
                                        )
                                    }
                                    {
                                        thread?.selectedOrder?.complaint?.referenceDocuments?.length < 1 && '-'
                                    }
                                </li>
                            </ul>
                        </ul>
                    </div>
                )
            ) : thread?.selectedFormType === "view-order" ? (
                createPortal(
                <div className="fixed inset-0 z-[100] bg-black/30 px-3 py-4 sm:px-6 lg:px-8">
                    <div className="relative mx-auto flex h-full min-h-0 w-full max-w-[1000px] flex-col overflow-hidden rounded-[12px] bg-white shadow-[0_24px_80px_rgba(15,23,42,0.25)]">
                        <button
                            className="absolute right-4 top-4 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-white text-blue-500 underline shadow-[0_8px_24px_rgba(15,23,42,0.12)]"
                            onClick={() => {
                                dispatch(setSelectedFormType("system"));
                            }}
                        >
                            <IoClose className="w-[24px] h-auto text-secondary" />
                        </button>
                        <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin scrollbar-webkit px-3 py-4 font-roboto sm:px-[44px] sm:py-[44px]">
                            <h2 className="text-lg font-bold mb-4 mt-2 font-roboto text-[18px] leading-5 text-primary text-center bg-light-blue py-3">
                                Order Summary
                            </h2>
                        <div className="mt-4 w-full">
                            <p className="font-inter text-[11px] font-semibold uppercase tracking-[0.12em] text-[#637189]">
                                Order Details
                            </p>
                            <ul className="mt-2 grid grid-cols-1 gap-x-6 xl:grid-cols-2">
                                {orderDetailFields.map((field) => (
                                    <React.Fragment key={field.label}>
                                        {renderPlainField(field)}
                                    </React.Fragment>
                                ))}
                            </ul>
                        </div>
                        <div className="mt-5 w-full">
                            <p className="font-inter text-[11px] font-semibold uppercase tracking-[0.12em] text-[#637189]">
                                Order Requirements
                            </p>
                            <ul className="mt-2 grid grid-cols-1 gap-x-6 xl:grid-cols-2">
                                {orderRequirementFields.map((field) => (
                                    <React.Fragment key={field.label}>
                                        {renderPlainField(field)}
                                    </React.Fragment>
                                ))}
                            </ul>

                            <div className="mt-3 w-full rounded-[12px] border border-[#dde6f5] bg-white px-2.5 py-3 shadow-[0_6px_18px_rgba(15,23,42,0.035)] sm:rounded-[14px] sm:px-4">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#4c62c7]">
                                        Reference Documents
                                    </p>
                                    <span className="rounded-full bg-[#f3f7ff] px-2.5 py-1 text-[11px] font-medium text-[#4f5f78]">
                                        {referenceDocuments.length || 0} file{referenceDocuments.length === 1 ? "" : "s"} attached
                                    </span>
                                </div>
                                <div className="mt-3 rounded-[10px] bg-white px-2 py-3 sm:rounded-[12px] sm:px-3">
                                    <ReferenceDocumentsList documents={referenceDocuments} />
                                </div>
                            </div>

                            <div className="mt-3 w-full rounded-[12px] border border-[#dde6f5] bg-white px-2.5 py-3 shadow-[0_6px_18px_rgba(15,23,42,0.035)] sm:rounded-[14px] sm:px-4">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#4c62c7]">
                                        Order Instructions
                                    </p>
                                    <span className="rounded-full bg-[#f3f7ff] px-2.5 py-1 text-[11px] font-medium text-[#4f5f78]">
                                        Shared in this thread
                                    </span>
                                </div>
                                {orderRequirements?.orderInstructions ? (
                                    <div
                                        className="order-instructions-content mt-3 overflow-x-auto rounded-[10px] bg-white px-2 py-3 text-[14px] leading-6 text-gray font-inter break-words sm:rounded-[12px] sm:px-3 sm:text-[13px] sm:leading-5 lg:max-w-[1100px] 2xl:max-w-[1200px] [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-6 [&_li]:my-1 [&_p]:my-1 [&_u]:underline"
                                        dangerouslySetInnerHTML={{ __html: sanitizeRichText(orderRequirements.orderInstructions) }}
                                    />
                                ) : (
                                    <div className="mt-3 overflow-x-auto rounded-[10px] bg-white px-2 py-3 text-[14px] leading-6 text-gray font-inter whitespace-pre-wrap break-words sm:rounded-[12px] sm:px-3 sm:text-[13px] sm:leading-5 lg:max-w-[1100px] 2xl:max-w-[1200px]">-</div>
                                )}
                            </div>
                        </div>
                        {
                            getVisibleAdditionalServices(thread?.selectedOrder?.orderRequirements?.aditionalServices).length > 0 && (
                            <>
                            <div className="mt-4 flex items-end justify-between gap-3">
                                <div>
                                    <h2 className="mt-1 font-roboto text-[20px] font-bold leading-6 text-primary">
                                        Additional services
                                    </h2>
                                </div>
                                <div className="rounded-full bg-[#eef4ff] px-3 py-1 text-xs font-medium text-[#1d4ed8]">
                                    {getVisibleAdditionalServices(thread?.selectedOrder?.orderRequirements?.aditionalServices).length || 0} selected
                                </div>
                            </div>
                            <div className="w-full rounded-[18px] border border-[#dbe4f2] bg-[#fbfdff] p-3 shadow-[0_14px_36px_rgba(15,23,42,0.05)]">
                                <div className="grid grid-cols-1 gap-2">
                                    {
                                        getVisibleAdditionalServices(thread?.selectedOrder?.orderRequirements?.aditionalServices).length > 0  &&  
                                        getVisibleAdditionalServices(thread?.selectedOrder?.orderRequirements?.aditionalServices).map((service) =>{
                                            const serviceTooltip = getAdditionalServiceTooltip(service);

                                            return (
                                            <div
                                                key={`${service.label}-${service.costType || "service"}`}
                                                className="flex flex-col gap-2 rounded-[16px] border border-[#e5ecfb] bg-white px-3 py-2.5 shadow-[0_8px_24px_rgba(15,23,42,0.04)] sm:flex-row sm:items-center sm:justify-between"
                                            >
                                                <div className="pr-2">
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <p className="text-[15px] font-semibold leading-5 text-secondary">
                                                            {getAdditionalServiceDisplayLabel(service)}
                                                            {service.label === ADITIONAL_SERVICE_LABELS.FIGURES &&
                                                                ` (${thread?.selectedOrder?.orderRequirements?.numberOfFigures})`}
                                                        </p>
                                                        {serviceTooltip && (
                                                            <span className="group/service-tooltip relative inline-flex">
                                                                <Info className="h-4 w-4 text-[#4c62c7]" aria-hidden="true" />
                                                                <span className="pointer-events-none absolute left-1/2 top-full z-30 mt-2 hidden w-[min(320px,calc(100vw-48px))] -translate-x-1/2 rounded-lg bg-slate-950 px-3 py-2 text-center text-xs font-medium leading-5 text-white shadow-xl group-hover/service-tooltip:block">
                                                                    {serviceTooltip}
                                                                </span>
                                                            </span>
                                                        )}
                                                    </div>
                                                    {service.label === ADITIONAL_SERVICE_LABELS.SPECIFIC_WRITER && (
                                                        <p className="mt-0.5 text-[12px] font-medium leading-4 text-gray">
                                                            ID: {thread?.selectedOrder?.orderRequirements?.requestedWriterId || "-"}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="self-start rounded-full bg-[#eef4ff] px-2.5 py-1.5 text-sm font-semibold text-[#1d4ed8] shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] sm:self-center">
                                                    <span className="mr-2 inline-flex h-4 w-4 items-center justify-center rounded-full bg-white text-[10px] text-[#1d4ed8] shadow-[0_1px_2px_rgba(15,23,42,0.08)]">
                                                        ✓
                                                    </span>
                                                    ${Number(AditionalServiceCostCalculator(
                                                        service, 
                                                        thread.selectedOrder?.basePrice, 
                                                        thread.selectedOrder?.costPerPage, 
                                                        thread.selectedOrder.orderRequirements?.pages
                                                    )).toFixed(2)}
                                                </div>
                                            </div>
                                            );
                                        })
                                    }
                                </div>
                            </div>
                            </>
                            )
                        }
                        </div>
                    </div>
                </div>,
                document.body
                )
            ) : thread?.selectedFormType === "accept-order" ? (
                renderDetailsPortal(
                    <div className="overflow-y-auto">
                    <div className="max-w-[800px] w-[100%] md:w-[70%] relative mx-auto flex flex-col items-center p-6 rounded-lg overflow-auto scrollbar-none">
                        <h2 className="text-lg font-semibold mb-3 mt-2 font-roboto text-[20px] leading-5 text-primary text-center py-3">
                            Accept Order
                        </h2>

                        <p className="text-sm flex items-center font-inter text-secondary max-w-[600px]  text-center ">
                            Are you sure you want to accept this order? Once
                            accepted, no further revisions can be requested.
                        </p>

	                        <div className="flex items-center justify-center mt-4 space-x-2 text-inter text-secondary">
	                            {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                    key={star}
                                    onClick={() => setRating(star)}
                                    className={`text-2xl  ${
                                        star <= rating
                                            ? "text-[#FBBF24]"
                                            : "text-gray"
                                    }`}
                                >
                                    <FaStar />
                                </button>
                            ))}
	                            <span className="text-inter text-secondary text-level-4">
	                                {rating}/5 stars
	                            </span>
	                        </div>

                            <div className="mt-6 flex w-full flex-wrap gap-2">
                                {quickReviewComments.map((comment) => (
                                    <button
                                        key={comment}
                                        type="button"
                                        onClick={() => handleQuickReviewSelect(comment)}
                                        className="rounded-full border border-[#BFDBFE] bg-[#EFF6FF] px-3 py-2 text-left font-inter text-level-5 font-medium text-[#1D4ED8] transition-colors duration-200 hover:bg-[#DBEAFE]"
                                    >
                                        {comment}
                                    </button>
                                ))}
                            </div>

	                        <textarea
	                            placeholder="Leave feedback about the delivered work"
                            value={feedback}
                            onChange={(e) => {setFeedback(e.target.value)}}
                            className="w-full text-inter text-gray text-level-4 rounded-[5px] border border-medium-gray p-3 focus:outline-none mt-[32px] min-h-[166px]"
                            rows="4"
                        ></textarea>

                        <div className="flex w-full justify-between mt-6">
                            <ArrowButton
                                text="Back"
                                onClick={() => {}}
                                width="auto"
                                height="44px"
                                iconDirection="left"
                                iconPosition="before"
                                classname="mb-[25px]"
                            />
                            
                            <LoginButton
                                text={
                                    isAcceptOrder ? 
                                    <ClipLoader
                                        loading={true}
                                        speedMultiplier={0.5}
                                        size={22}
                                        color="#60a5fa"
                                    />
                                    : "Accept Order"}               
                                type="submit"
                                className="w-[80px] sm:w-[170px] h-[44px] flex items-center justify-center rounded-[5px] font-roboto text-[10px] sm:text-level-3 text-white font-semibold gap-[12px] px-[30px] py-[10px] transition-all duration-200"
                                onClick={() => {handleAcceptOrder()}}
                                disabled = {isAcceptOrder}
                            />
                        </div>
                    </div>
                </div>
                )
            ) : thread?.selectedFormType === "request-revision" ? (
                renderDetailsPortal(
                    <>
                    <div className="max-w-[800px] w-[100%] md:w-[70%] relative mx-auto flex flex-col items-center px-6 pt-6 pb-0 rounded-lg overflow-auto scrollbar-none">
                        <h2 className="text-lg font-semibold mb-3 mt-2 font-roboto text-[20px] leading-5 text-primary text-center py-3">
                            Request Revision
                        </h2>

                        <p className="text-level-4 flex items-center font-inter text-secondary max-w-[600px]  text-center mb-4 ">
                            Specify the changes or corrections required for this
                            order.
                        </p>

                        <div className="mb-6 w-full rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-3 font-inter text-[13px] leading-5 text-secondary">
                            Revisions must relate to the original instructions. New topics, added pages, new sources, changed rubrics, or citation-style changes may require extra payment.
                            <a
                                href="/terms-and-conditions#revision-policy"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ml-1 font-semibold text-blue-shades underline"
                            >
                                Read revision policy
                            </a>
                        </div>

                        {/* <Input
                            label="Changes / Requirements"
                            id="subject"
                            placeholder="Select the Subject"
                            inputStyle="w-full  max-h-[166px] "
                        /> */}
                        <LoginInput
                            className="w-full 2xl:w-full px-4"
                            id="requestRevisionRequirements"
                            name="requestRevisionRequirements"
                            label="Changes / Requirements"
                            type="textarea"
                            value={formData?.requestRevisionRequirements}
                            placeholder="List specific changes"
                            error={touched?.requestRevisionRequirements && errors?.requestRevisionRequirements}
                            onChange={(e) => handleInputChange("requestRevisionRequirements", e.target.value)}
                            onBlur={() => handleBlur("requestRevisionRequirements", formData.requestRevisionRequirements)}
                            isRequired={true}
                            isFloatingLabel={false}
                            outerContainerStyle="w-full"
                            inputStyle="h-[160px] max-h-[320px] resize-y overflow-y-auto"
                        />

                        <h2 className="mt-[24px] mb-[6px] w-full font-inter text-secondary text-level-3 font-medium leading-6 text-start">
                            Supporting Files for This Revision
                        </h2>
                        <p className="mb-[12px] w-full font-inter text-[13px] leading-5 text-gray text-start">
                            Upload marked files, screenshots, rubric comments, or instructor feedback if available.
                        </p>
												{uploadFiles?.length > 0 &&
													<div className="w-full flex flex-wrap gap-2 lg:gap-4 m-2 lg:m-4">
														{
															uploadFiles.map((file, index) => {
																return <CustomFileIcon 
																					name={file.name} 
																					extension={file.name.split('.').pop().toLowerCase()}
																					downloadable={false}
																					removeFile={() => setUploadFiles(prev => prev.filter((f, i) => i !== index))}
																				/>
															})
														}
													</div>
												}
                            <DragUpload 
                                onFileUpload={handleDragUpload} 
                                isLoading={isUploadRefrencesToFirebaseLoading}
                                uploadFiles={uploadFiles}
                                dragText="Drag your file(s) to start uploading"
                                uploadText=""
                            />
                        <div className="flex w-full justify-between mt-6">
                            <ArrowButton
                                text="Back"
                                onClick={() => {dispatch(setSelectedFormType("system"));}}
                                width="auto"
                                height="44px"
                                iconDirection="left"
                                iconPosition="before"
                                classname=""
                            />
                            <LoginButton
                                text={
                                    isRequestRevision || isUploadRefrencesToFirebaseLoading ? 
                                    <ClipLoader
                                        loading={true}
                                        speedMultiplier={0.5}
                                        size={22}
                                        color="#60a5fa"
                                    />
                                    : "Request Revision"
                                }               
                                type="submit"
                                className="w-[80px] sm:w-[223px] h-[44px] flex items-center justify-center rounded-[5px] font-roboto text-[10px] sm:text-level-3 text-white font-semibold gap-[12px] px-[30px] py-[10px] transition-all duration-200"
                                onClick={() => {handleRequestRevision()}}
                                disabled = {isRequestRevision}
                            />
                        </div>
                    </div>
                </>
                )
            ) : thread?.selectedFormType === "deliver-order" ? (
                <>
                    <div className="max-w-[800px] relative mx-auto flex flex-col items-center px-6 pt-6 pb-0 rounded-lg overflow-hidden"></div>
                </>
            ) : thread?.selectedFormType === "system" ? (
                <div ref={systemNotificationScrollContainerRef} className="overflow-y-auto pr-2">
                    {thread?.selectedThreadNotifications?.map(
                        (group, indexmain ,arraymain) => {
                            return (
                                <>
                                    <div key={indexmain}  >
                                        {/* Display the date */}
                                        <div className="flex items-center justify-center my-4 mx-8">
                                            <div className="flex-grow h-px bg-[#E6E7EC]"></div>
                                            <div className="mx-4 font-inter font-medium text-level-5 text-gray">
                                                {group?._id}
                                            </div>
                                            <div className="flex-grow h-px bg-[#E6E7EC]"></div>
                                        </div>

                                        {/* Render system messages only once per date */}
                                        {group?.messages?.length > 0 && (
                                            <div className="system-messages z-5">
                                                {group?.messages?.map(
                                                    (message, index, array) => {

                                                        // there can be multiple revisions and multiple deliverables setGroupedInboxMessagesByDate. Hence we need to render only the related result for revision on deleiverables
                                                        let revisionReference = null;

                                                        // tracking related deliverables object
                                                        if(message.content === systemNotifications.ORDER_DELIVERED){
                                                            revisionReference = 
                                                            multipleRevisions?.length - 1  > -1 ? 
                                                            thread?.selectedOrder?.revisions[multipleRevisions?.length - 1]?._id : 
                                                            {
                                                                _id : null, 
                                                                obj: {
                                                                    deliveryNotes: thread?.selectedOrder?.deliveryNotes,
                                                                    finalDeliverables: thread?.selectedOrder?.finalDeliverables
                                                                }
                                                            }; 
                                                        }

                                                       // tracking related revision object
                                                        if(message.content === systemNotifications.REQUEST_REVISION){
                                                            revisionReference = thread?.selectedOrder?.revisions[multipleRevisions?.length]?._id;
                                                            multipleRevisions.push({obj: thread?.selectedOrder?.revisions[multipleRevisions?.length], _id: revisionReference})
                                                        }

                                                        return (
                                                            <SystemMessage
                                                                handleButtonClick={
                                                                    handleButtonClick
                                                                }
                                                                key={`system-notification${group?._id}-${index}`}
                                                                message={
                                                                    message.content
                                                                }
                                                                revisionReference={revisionReference}
                                                                providedSolution={thread?.selectedOrder?.complaint?.providedResolution}
                                                                date={
                                                                    message.createdAt
                                                                }
                                                                // time={message.time}
                                                                isLast={
                                                                    index ===
                                                                    array.length - 1 && indexmain === arraymain.length - 1
                                                                }
                                                                isLastWithinGroup = {
                                                                    index ===
                                                                    array.length - 1
                                                                }
                                                                isSingleInGroup={
                                                                    array.length ===
                                                                    1
                                                                }
                                                            />
                                                        );
                                                    }
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </>
                            );
                        }
                    )}
                    <div ref={systemNotificationEndRef} aria-hidden="true" />
                </div>
            ) : thread?.selectedFormType === "message" ? (
                <div ref={messageScrollContainerRef} className="overflow-y-auto pr-2 pb-[40px] xl:pr-4">
                    { 
                      isFetchUserOrderByRoomId || isFetchThreadById || isFetchUserThreadChatsLoading || isFetchUserThreadSystemNotificationsLoading ? (
                        <div className="flex flex-col flex-grow items-center justify-center w-full">
                            <ClipLoader
                                loading={true}
                                speedMultiplier={0.5}
                                size={82}
                                color="#60a5fa"
                            />
                        </div>
                    ) : Object.keys(groupedInboxMessagesByDate)?.length > 0 ? (
                        sortGroupdArray(
                            Object.keys(groupedInboxMessagesByDate)
                        )?.map((date, index, dates) => {
                            const isLastDateGroup = index === dates.length - 1;
                            return (
                                <>
                                    <div key={`inbox-messages-${date}`} className="mb-2">
                                        <div className="flex items-center justify-center my-4 mx-8">
                                            <div className="flex-grow h-px bg-[#E6E7EC]"></div>
                                            <div className="mx-4 font-inter font-medium text-level-5 text-gray">
                                                {date}
                                            </div>
                                            <div className="flex-grow h-px bg-[#E6E7EC]"></div>
                                        </div>
            
                                        {(
                                            groupedInboxMessagesByDate[date] || []
                                        )?.map((message, index, messages) => (
                                            <div
                                                id={`order-message-${message?._id}`}
                                                key={`inbox-message-${date}-${index}`}
                                                className={`flex mt-3 ${isLastDateGroup && index === messages.length - 1 ? "mb-4" : ""}`}
                                            >
                                                <div className="flex w-full">
                                                    {/* flex-row-reverse */}
                                                    <div className={twMerge("flex w-full", tokenPayload?._id?.toString() === message?.sender?.toString() ? "flex-row-reverse" : "")}>
                                                        <img
                                                            src={
                                                                "https://static.vecteezy.com/system/resources/thumbnails/002/002/403/small/man-with-beard-avatar-character-isolated-icon-free-vector.jpg"
                                                            }
                                                            alt={message.sender}
                                                            className={`w-6 h-6 ml-2 rounded-full ${
                                                                message.sender === "Admin"
                                                                    ? "mt-[28px]"
                                                                    : "mt-[4px]"
                                                            }`}
                                                        />
                                                        {/* flex-row-reverse */}
                                                        <div className="flex flex-col mb-2 ml-4">
                                                            <div>
                                                                <span className="font-inter font-medium text-secondary text-level-5 leading-[14.52px]">
                                                                    {messageSenderLabel(message)}
                                                                </span>
                                                            </div>
                                                            {/* rounded-tl-[16px] rounded-tr-none */}
                                                            <div
                                                                className={`max-w-[78vw] xl:max-w-[680px] break-words whitespace-pre-wrap font-inter font-gray font-normal text-level-4 leading-[20px] px-[16px] py-[11px] text-gray mt-[3px] inline-block rounded-b-[16px] ${
                                                                    message.sender?.toString() ===
                                                                    tokenPayload?._id?.toString()
                                                                        ? "bg-soft-blue text-primary"
                                                                        : "bg-soft-gray"
                                                                } ${tokenPayload?._id?.toString() === message?.sender?.toString() ? "rounded-tl-[16px] rounded-tr-none" : "rounded-tl-none rounded-tr-[16px]" }`}
                                                            >
                                                                {renderReplyReference(message)}
                                                                {message.content}
                                                                {
                                                                    message?.attachments?.length > 0 && (
                                                                        <>
                                                                            <div className="flex flex-col gap-2">
                                                                                {
                                                                                    message?.attachments?.map((url, index) => {
                                                                                        return(
                                                                                            <>
                                                                                                <CustomFileIcon extension={getFileExtension(url)} key={index} url={url}/>
                                                                                            </>
                                                                                        )
                                                                                    })
                                                                                }
                                                                            </div>
                                                                        </>
                                                                    )
                                                                }
                                                            </div>
                                                            <span className={`ml-[8px] font-inter text-gray italic text-[10px] leading-[20px]  py-[2px] ${tokenPayload?._id?.toString() === message?.sender?.toString() ? "text-left -translate-x-5" : "text-right translate-x-5"}`}>
                                                                {formatHH_MM_SS(
                                                                    message.createdAt
                                                                )}
                                                                <MessageReadReceipt
                                                                    message={message}
                                                                    selectedThread={thread?.selectedThread}
                                                                    currentUserId={tokenPayload?._id}
                                                                />
                                                                {![orderStatus.COMPLETED, orderStatus.RESOLVED, orderStatus.CANCELLED].includes(thread?.selectedOrder?.status) && <button
                                                                    type="button"
                                                                    onClick={() => { setReplyToMessage(message); messageInputRef.current?.focus(); }}
                                                                    className="ml-2 inline-flex align-middle not-italic font-semibold text-primary hover:underline"
                                                                    title="Reply to message"
                                                                >
                                                                    Reply
                                                                </button>}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* {unreadCount > 0 && (
                                        <>
                                            <div
                                                onClick={
                                                    scrollToFirstUnread
                                                }
                                                className="flex items-center cursor-pointer justify-center absolute bottom-[76px] right-6 w-8 h-8 rounded-full bg-[#E6E7EC]"
                                            >
                                                <img
                                                    src={icon}
                                                    alt="Scroll to unread"
                                                />
                                            </div>

                                            <div className="absolute flex justify-center items-center bottom-[98px] z-100 right-4 bg-blue-shades h-5 w-5 rounded-full">
                                                <p className="text-white font-inter text-level-4 leading-[21px]">
                                                    {unreadCount}
                                                </p>
                                            </div>
                                        </>
                                    )} */}

                                    <div
                                        className={`absolute bottom-[10px] ml-0 mr-0 flex w-full bg-white px-2 rounded-[12px] sm:mr-[24px] sm:w-[98%] sm:px-0 sm:pr-6 ${
                                            [
                                                orderStatus.COMPLETED,
                                                orderStatus.RESOLVED,
                                            ].includes(
                                                thread
                                                    ?.selectedOrder
                                                    ?.status
                                            )
                                                ? "opacity-50 pointer-events-none"
                                                : ""
                                        }`}
                                    >
                                        {renderReplyComposerPreview()}
                                        {renderTypingIndicator()}
                                        <div className="relative justify-center mt-3 ">
                                        {!isUploadRefrencesToFirebaseLoading && 
                                                <button
                                                    type="button"
                                                    className="mr-2 flex h-[48px] w-[48px] items-center justify-center rounded-[10px] border border-[#cbd8ea] bg-[#f8fbff] font-inter text-[12px] font-medium text-secondary transition-colors duration-200 hover:border-[#b9c7de] hover:bg-[#eef4ff] sm:mr-[12px] sm:h-[40px] sm:w-auto sm:gap-2 sm:rounded-[8px] sm:px-3"
                                                    title="Upload Attachments"
                                                    onClick={() => {
                                                        if (
                                                            ![
                                                                orderStatus.COMPLETED,
                                                                orderStatus.RESOLVED,
                                                            ].includes(
                                                                thread
                                                                    ?.selectedOrder
                                                                    ?.status
                                                            )
                                                        ) {
                                                            document
                                                                .getElementById(
                                                                    "fileInput"
                                                                )
                                                                .click(); // Simulate file input click
                                                        }
                                                    }}
                                                >
                                                    <img
                                                        src={attachicon}
                                                        alt=""
                                                        className="h-auto w-[18px] sm:w-[13px]"
                                                    />
                                                    <span className="hidden sm:inline">Attach</span>
                                                </button>
                                            }
                                            {isUploadRefrencesToFirebaseLoading && <ClipLoader
                                                loading={true}
                                                speedMultiplier={1}
                                                size={22}
                                                color="#60a5fa"
                                            />}
                                            {!isUploadRefrencesToFirebaseLoading && <span className=" w-[12px] h-[12px] rounded-full bg-blue-400 text-[8px] font-inter text-white flex items-center justify-center absolute -top-2 right-3">
                                                {uploadFiles.length || 0}
                                            </span>}
                                            {!isUploadRefrencesToFirebaseLoading && <span className="w-[12px] h-[12px] rounded-full bg-slate-400 text-[8px] font-inter text-white flex items-center justify-center absolute top-2 right-1">
                                                <button
                                                    className="text-blue-500 underline flex justify-center items-center"
                                                    onClick={() => {
                                                        setUploadFiles([]);
                                                    }}
                                                    title="Clear All Attachments"
                                                >
                                                    <IoClose className="w-[8px] h-auto text-white" />
                                                </button>
                                            </span>}
                                            <input
                                                type="file"
                                                id="fileInput"
                                                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.txt,.csv,.zip,.rar,.7z"
                                                className="hidden"
                                                multiple // Allow multiple file selection
                                                onChange={(e) => {
                                                    if (e.target.files.length > 0) {
                                                        const files = Array.from(e.target.files);
                                                        setUploadFiles([...uploadFiles, ...files]);

                                                        // Reset the input value to allow re-uploading the same files
                                                        e.target.value = null;
                                                    }
                                                }}
                                            />
                                        </div>
																				
																				<div className="relative flex w-full items-end mr-4 xl:mr-1">
																					{	uploadFiles?.length > 0 && 
																						<div className={`absolute z-5 w-[100%] top-0 translate-y-[-100%] max-h-[150px] overflow-y-auto border-[1px] border-medium-gray rounded-t-[12px] bg-[#FFFFFF] ${uploadFiles?.length > 0 ? "border-b-0 rounded-none": ""}`}>
																							<div className="flex flex-wrap  gap-2 lg:gap-4 m-2 lg:m-4">
																								{
																									uploadFiles.map((file, index) => {
																										return <CustomFileIcon 
																															name={file.name} 
																															extension={file.name.split('.').pop().toLowerCase()}
																															downloadable={false}
																															removeFile={() => setUploadFiles(prev => prev.filter((f, i) => i !== index))}
																														/>
																									})
																								}
																							</div>
																						</div>
																					}
																					<textarea
																							placeholder="Type a message"
																							value={text}
																								ref={messageInputRef}
																								onChange={handleMessageTextChange}
																						disabled={isMessageSending || [
																									orderStatus.COMPLETED,
																									orderStatus.RESOLVED,
                                                                                                    orderStatus.CANCELLED
																							].includes(
																									thread?.selectedOrder?.status
																							)}
																								rows={1}
																								className={`flex-grow w-[98%] min-h-[44px] max-h-[112px] resize-none overflow-y-auto border border-t-medium-gray border-b-medium-gray border-r-medium-gray border-l-medium-gray rounded-l-[12px] px-3 py-2.5 focus:outline-none focus:border-[#CCCED2] font-inter text-level-4 leading-[22px] text-gray whitespace-pre-wrap break-words sm:min-h-[52px] sm:max-h-[128px] sm:px-4 sm:py-3
														disabled:bg-soft-gray disabled:text-gray-400 disabled:border-gray-300 disabled:cursor-not-allowed
														${uploadFiles?.length > 0 && "border-t-0 border-r-0 rounded-tl-none"}`}
																							onKeyDown={e => {
																									if(e.key === "Enter" && !e.shiftKey){
																											e.preventDefault();
																											handleSendMessage();
																									}
																							}}
																					/>
																					<button
																							onClick={
																									handleSendMessage
																							}
																							disabled={[
																									orderStatus.COMPLETED,
																									orderStatus.RESOLVED,
                                                                                                    orderStatus.CANCELLED
																							].includes(
																									thread?.selectedOrder?.status
																							)}
																								className={`border border-t-medium-gray border-b-medium-gray border-r-medium-gray border-l-medium-gray text-gray-700 px-3 py-3 min-h-[52px] rounded-r-[12px] focus:outline-none focus:border-[#CCCED2] sm:min-h-[88px] sm:px-4
																										disabled:bg-soft-gray disabled:text-gray-400 disabled:border-gray-300 disabled:cursor-not-allowed
																										${uploadFiles?.length > 0 && "border-t-0 border-l-0 rounded-tr-none"}`}
																							>
                                                                                            {(isUploadRefrencesToFirebaseLoading || isMessageSending) && (
                                                                                                <ClipLoader
                                                                                                    loading={true}
                                                                                                    speedMultiplier={1}
                                                                                                    size={22}
                                                                                                    color="#60a5fa"
                                                                                                />
                                                                                            )}
                                                                                            {!isUploadRefrencesToFirebaseLoading && !isMessageSending && (
                                                                                                <img
                                                                                                src={sendicon}
                                                                                                alt="Send Icon"
																							/>
                                                                                            )}
																					</button>
																				</div>
                                    </div>

                                                </div>
                                            </div>
                                        ))}
                                        {isLastDateGroup && <div ref={messageEndRef} className="h-[48px]" />}
                                    </div>
                                </>
                            );
                        })
                    ) : (
                        <div className="flex flex-col font-inter justify-center items-center h-[60vh]">
                            <img
                                src={waiting} // Replace with your waiting image URL
                                alt="Waiting for messages"
                                className="w-[250px] h-auto"
                            />
                            <h1 className="text-level-2 font-roboto text-primary font-semibold">No Messages , yet</h1>
                            <p className=" min-w-[20vh]  sm:w-[40vh] lg:w-[60vh] text-level-3 text-gray mt-2 text-center">Your inbox is currently empty. Once you receive messages, they’ll appear here.</p>
                            {thread?.selectedOrder?.status !== orderStatus.PENDING_PAYMENT && (
                                <div
                                    className={`absolute bottom-[10px] ml-0 mr-0 flex w-full bg-white px-2 rounded-[12px] sm:ml-12 sm:mr-[24px] sm:w-[98%] sm:px-0 sm:pr-6 ${
                                        [
                                            orderStatus.COMPLETED,
                                            orderStatus.RESOLVED,
                                        ].includes(
                                            thread
                                                ?.selectedOrder
                                                ?.status
                                        )
                                            ? "opacity-50 pointer-events-none"
                                            : ""
                                    }`}
                                >
                                    {renderReplyComposerPreview()}
                                    {renderTypingIndicator()}
                                    <div className="relative justify-center mt-3 ">
                                    {!isUploadRefrencesToFirebaseLoading && 
                                            <button
                                                type="button"
                                                className="mr-2 flex h-[48px] w-[48px] items-center justify-center rounded-[10px] border border-[#cbd8ea] bg-[#f8fbff] font-inter text-[12px] font-medium text-secondary transition-colors duration-200 hover:border-[#b9c7de] hover:bg-[#eef4ff] sm:mr-[12px] sm:h-[40px] sm:w-auto sm:gap-2 sm:rounded-[8px] sm:px-3"
                                                title="Upload Attachments"
                                                onClick={() => {
                                                    if (
                                                        ![
                                                            orderStatus.COMPLETED,
                                                            orderStatus.RESOLVED,
                                                        ].includes(
                                                            thread
                                                                ?.selectedOrder
                                                                ?.status
                                                        )
                                                    ) {
                                                        document
																													.getElementById(
																															"fileInput"
																													)
																														.click(); // Simulate file input click
                                                    }
                                                }}
                                            >
                                                <img
                                                    src={attachicon}
                                                    alt=""
                                                    className="h-auto w-[18px] sm:w-[13px]"
                                                />
                                                <span className="hidden sm:inline">Attach</span>
                                            </button>
                                        }
                                        {isUploadRefrencesToFirebaseLoading && <ClipLoader
                                            loading={true}
                                            speedMultiplier={1}
                                            size={22}
                                            color="#60a5fa"
                                        />}
                                        {!isUploadRefrencesToFirebaseLoading && <span className=" w-[12px] h-[12px] rounded-full bg-blue-400 text-[8px] font-inter text-white flex items-center justify-center absolute -top-2 right-3">
                                            {uploadFiles.length || 0}
                                        </span>}
                                        {!isUploadRefrencesToFirebaseLoading && <span className="w-[12px] h-[12px] rounded-full bg-slate-400 text-[8px] font-inter text-white flex items-center justify-center absolute top-2 right-1">
                                            <button
                                                className="text-blue-500 underline flex justify-center items-center"
                                                onClick={() => {
                                                    setUploadFiles([]);
                                                }}
                                                title="Clear All Attachments"
                                            >
                                                <IoClose className="w-[8px] h-auto text-white" />
                                            </button>
                                        </span>}
                                        <input
                                            type="file"
                                            id="fileInput"
                                            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.txt,.csv,.zip,.rar,.7z"
                                            className="hidden"
                                            multiple // Allow multiple file selection
                                            onChange={(e) => {
                                                if (e.target.files.length > 0) {
                                                    const files = Array.from(e.target.files);
                                                    setUploadFiles([...uploadFiles, ...files]);
                                                }

                                                // Reset the input value to allow re-uploading the same files
                                                e.target.value = null;
                                            }}
                                        />
                                    </div>
																					
                                    <div className="relative flex w-full items-end mr-4 xl:mr-1">
																					{	uploadFiles?.length > 0 && 
																						<div className={`absolute z-5 w-[100%] top-0 translate-y-[-100%] max-h-[150px] overflow-y-auto border-[1px] border-medium-gray rounded-t-[12px] bg-[#FFFFFF] ${uploadFiles?.length > 0 ? "border-b-0 rounded-none": ""}`}>
																							<div className="flex flex-wrap  gap-2 lg:gap-4 m-2 lg:m-4">
																								{
																									uploadFiles.map((file, index) => {
																										return <CustomFileIcon 
																															name={file.name} 
																															extension={file.name.split('.').pop().toLowerCase()}
																															downloadable={false}
																															removeFile={() => setUploadFiles(prev => prev.filter((f, i) => i !== index))}
																														/>
																									})
																								}
																							</div>
																						</div>
																					}
																					<textarea
																							placeholder="Type a message"
																							value={text}
																								ref={messageInputRef}
																								onChange={handleMessageTextChange}
																						disabled={isMessageSending || [
																									orderStatus.COMPLETED,
																									orderStatus.RESOLVED,
                                                                                                    orderStatus.CANCELLED
																							].includes(
																									thread?.selectedOrder?.status
																							)}
																								rows={1}
																								className={`flex-grow w-[98%] min-h-[44px] max-h-[112px] resize-none overflow-y-auto border border-t-medium-gray border-b-medium-gray border-r-medium-gray border-l-medium-gray rounded-l-[12px] px-3 py-2.5 focus:outline-none focus:border-[#CCCED2] font-inter text-level-4 leading-[22px] text-gray whitespace-pre-wrap break-words sm:min-h-[52px] sm:max-h-[128px] sm:px-4 sm:py-3
														disabled:bg-soft-gray disabled:text-gray-400 disabled:border-gray-300 disabled:cursor-not-allowed
														${uploadFiles?.length > 0 && "border-t-0 border-r-0 rounded-tl-none"}`}
																							onKeyDown={e => {
																									if(e.key === "Enter" && !e.shiftKey){
																											e.preventDefault();
																											handleSendMessage();
																									}
																							}}
																					/>
																					<button
																							onClick={
																									handleSendMessage
																							}
																							disabled={[
																									orderStatus.COMPLETED,
																									orderStatus.RESOLVED,
                                                                                                    orderStatus.CANCELLED
																							].includes(
																									thread?.selectedOrder?.status
																							)}
																								className={`border border-t-medium-gray border-b-medium-gray border-r-medium-gray border-l-medium-gray text-gray-700 px-3 py-3 min-h-[52px] rounded-r-[12px] focus:outline-none focus:border-[#CCCED2] sm:min-h-[88px] sm:px-4
																										disabled:bg-soft-gray disabled:text-gray-400 disabled:border-gray-300 disabled:cursor-not-allowed
																										${uploadFiles?.length > 0 && "border-t-0 border-l-0 rounded-tr-none"}`}
																							>
                                                                                            {(isUploadRefrencesToFirebaseLoading || isMessageSending) && (
                                                                                                <ClipLoader
                                                                                                    loading={true}
                                                                                                    speedMultiplier={1}
                                                                                                    size={22}
                                                                                                    color="#60a5fa"
                                                                                                />
                                                                                            )}
                                                                                            {!isUploadRefrencesToFirebaseLoading && !isMessageSending && (
                                                                                                <img
                                                                                                    src={sendicon}
                                                                                                    alt="Send Icon"
																							    />
                                                                                            )}

																					</button>
																				</div>
                                </div>
                            )}
                        </div>
                    )
                    }
                </div>
            ) : thread?.selectedFormType === "make-extra-payment" ? (
                renderDetailsPortal(
                    <MakeExtraPaymentModal thread = {thread} hideCloseButton/>
                )
            ) : thread?.selectedFormType === "tip-writer" ? (
                renderDetailsPortal(
                    <MakeExtraPaymentModal thread={thread} mode="tip" hideCloseButton/>
                )
            ) : (
                <></>
            )}
        </div>
    );
};

export default MessageList;
